import { AnnotationTemplateClient } from "@formant/core/client/AnnotationTemplateClient";
import { defined } from "@formant/core/common/defined";
import { hasMatchingTags } from "@formant/core/common/hasMatchingTags";
import { isErrorType } from "@formant/core/common/isErrorType";
import { logger } from "@formant/core/common/logger";
import { ResponseError } from "@formant/core/common/ResponseError";
import { DefinedEntity } from "@formant/core/model/DefinedEntity";
import { IAnnotationFieldValue } from "@formant/core/model/IAnnotationFieldValue";
import { IAnnotationFieldValuesRequest } from "@formant/core/model/IAnnotationFieldValuesRequest";
import { IAnnotationTemplate } from "@formant/core/model/IAnnotationTemplate";
import { IDevice } from "@formant/core/model/IDevice";
import { IDeviceDetails } from "@formant/core/model/IDeviceDetails";
import { ITags } from "@formant/core/model/ITags";
import { Uuid } from "@formant/core/model/Uuid";

import { IAnnotationTemplateStore } from "./IAnnotationTemplateStore";
import { IAuthStore } from "./IAuthStore";
import { IOrganizationStore } from "./IOrganizationStore";
import { StoreCache } from "./StoreCache";

export class AnnotationTemplateStore implements IAnnotationTemplateStore {
    private listCache = new StoreCache<
        undefined,
        DefinedEntity<IAnnotationTemplate>[]
    >();

    private cache = new StoreCache<
        Uuid,
        DefinedEntity<IAnnotationTemplate> | false
    >();

    private fieldValueQueryCache = new StoreCache<
        IAnnotationFieldValuesRequest,
        IAnnotationFieldValue[]
    >();

    constructor(
        private annotationTemplateClient: AnnotationTemplateClient,
        private authStore: IAuthStore,
        private organizationStore: IOrganizationStore,
    ) {}

    public list(): DefinedEntity<IAnnotationTemplate>[] | undefined {
        return this.listAll()?.filter(_ => _.enabled);
    }

    public listAll(): DefinedEntity<IAnnotationTemplate>[] | undefined {
        return this.listCache.get(
            undefined,
            async () => await this.fetchList(),
        );
    }

    public listMatching(
        tags: ITags,
    ): DefinedEntity<IAnnotationTemplate>[] | undefined {
        const organization = this.organizationStore.authenticated();
        if (!organization) {
            return;
        }
        const googleIntegrationExists = !!organization.googleInfo;
        return this.list()?.filter(
            _ =>
                // Returned templates must have matching tags
                hasMatchingTags(_.tags, tags) &&
                // And must not contain any sheet fields (unless Sheets is integrated)
                (googleIntegrationExists ||
                    !_.fields.some(({ type }) => type === "sheet")),
        );
    }

    public async fetchListMatching(
        tags: ITags,
    ): Promise<DefinedEntity<IAnnotationTemplate>[] | undefined> {
        const organization = this.organizationStore.authenticated();
        if (!organization) {
            return undefined;
        }
        const googleIntegrationExists = !!organization.googleInfo;
        return (await this.fetchList())?.filter(
            _ =>
                // Returned templates must have matching tags
                hasMatchingTags(_.tags, tags) &&
                // And must not contain any sheet fields (unless Sheets is integrated)
                (googleIntegrationExists ||
                    !_.fields.some(({ type }) => type === "sheet")),
        );
    }

    public get(
        id: Uuid,
    ): DefinedEntity<IAnnotationTemplate> | false | undefined {
        return this.cache.get(id, async () => {
            try {
                return await this.fetchGet(id);
            } catch (err) {
                if (
                    isErrorType(
                        err,
                        ResponseError,
                        ({ statusCode }) => statusCode === 422,
                    )
                ) {
                    logger.warn("Could not access annotation template", id);
                } else {
                    throw err;
                }
            }
            return false;
        });
    }

    public fieldValuesQuery(
        query: IAnnotationFieldValuesRequest,
    ): IAnnotationFieldValue[] | undefined {
        return this.fieldValueQueryCache.get(
            query,
            async () => await this.fetchFieldValues(query),
        );
    }

    public async create(
        annotationTemplate: IAnnotationTemplate,
    ): Promise<DefinedEntity<IAnnotationTemplate>> {
        const created = await this.annotationTemplateClient.create(
            await this.authStore.token(),
            annotationTemplate,
        );
        this.cache.set(defined(created.id), created);
        this.listCache.clear();
        return created;
    }

    public async update(
        id: Uuid,
        annotationTemplate: Partial<IAnnotationTemplate>,
    ): Promise<DefinedEntity<IAnnotationTemplate>> {
        const updated = await this.annotationTemplateClient.update(
            await this.authStore.token(),
            id,
            annotationTemplate,
        );
        this.cache.set(id, updated);
        this.listCache.clear();
        return updated;
    }

    public save(
        annotationTemplate: IAnnotationTemplate,
    ): Promise<DefinedEntity<IAnnotationTemplate>> {
        const { id } = annotationTemplate;
        if (id) {
            return this.update(id, annotationTemplate);
        } else {
            return this.create(annotationTemplate);
        }
    }

    public async disable(
        id: Uuid,
    ): Promise<DefinedEntity<IAnnotationTemplate>> {
        return await this.update(id, { enabled: false });
    }

    public existsForDevice(device: IDevice | IDeviceDetails): boolean {
        const matching = this.listMatching(device.tags);
        if (!matching || !matching.length) {
            return false;
        }
        return true;
    }

    async fetchGet(
        id: Uuid,
    ): Promise<DefinedEntity<IAnnotationTemplate> | false> {
        const annotationTemplate = await this.annotationTemplateClient.get(
            await this.authStore.token(),
            id,
        );
        const value = annotationTemplate || false;
        this.cache.set(id, value);
        return value;
    }

    public async fetchList(): Promise<DefinedEntity<IAnnotationTemplate>[]> {
        const value = await this.annotationTemplateClient.list(
            await this.authStore.token(),
        );
        this.listCache.set(undefined, value);
        value.forEach(_ => this.cache.set(defined(_.id), _));
        return value;
    }

    private async fetchFieldValues(
        query: IAnnotationFieldValuesRequest,
    ): Promise<IAnnotationFieldValue[]> {
        const value = await this.annotationTemplateClient.query(
            await this.authStore.token(),
            query,
        );
        this.fieldValueQueryCache.set(query, value);
        return value;
    }
}
